import _filter from 'lodash/filter';
import _map from 'lodash/map';
import {DateTime} from 'luxon';
import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import ObjectStatus from '../enums/static/ObjectStatus';
import {ProductType} from '../products/products.enums';
import {UTCTimezoneObject} from '../timezones/timezones.consts';
import {TimezoneObject} from '../timezones/timezones.types';
import getTimeZoneStringForDateTime from '../timezones/utils/getTimezoneStringForDateTime';
import {
  DataSourcesToClientModel,
  toClientDataSourceStatistics,
  toDataSourceStatisticsPixelData,
} from './dataSources.dataConverters';
import {
  AddDataSourceParams,
  DataSource,
  DataSourceMapping,
  DatasourceRuleOption,
  DataSourceStatistics,
  PixelData,
  RemoteDataSource,
  RemoteDataSourceStatistics,
} from './dataSources.types';
import isUserProfilingRelatedDataSourceType from './utils/isUserProfilingRelatedDataSourceType';

const basePath = 'DataSources';

async function get(): Promise<DataSource[]> {
  const {
    data: [dataSource],
  } = await apiService.executeGetRequest<RemoteDataSource[][]>(`${basePath}/Get`);

  return DataSourcesToClientModel(dataSource);
}

async function getDataSources(): Promise<DataSource[]> {
  const {data: dataSources} = await apiV2Service.executeGetRequest<RemoteDataSource[]>(
    `${basePath}`,
  );

  return DataSourcesToClientModel(dataSources);
}

async function deleteDataSource(id: number): Promise<void> {
  const {data} = await apiV2Service.executeDeleteRequest<void>(`${basePath}/${id}`);

  return data;
}

async function add({name, type, partnerId}: AddDataSourceParams): Promise<number> {
  const {data} = await apiService.executePostRequest<number>(`${basePath}/Add`, {
    name,
    type,
    ...(!!partnerId && {partnerId}),
  });

  return data;
}

async function update({
  dataSourceId,
  status,
  name,
}: {
  dataSourceId: number;
  status?: ObjectStatus;
  name?: string;
}) {
  return apiService.executePostRequest(`${basePath}/Update`, {
    dataSourceId,
    ...(status !== undefined && {status}),
    ...(name && {name}),
  });
}

async function getDatasourceSubTypes(): Promise<string[]> {
  const {
    data: [conversionSubTypes],
  } = await apiService.executeGetRequest<string[][]>(`${basePath}/getDatasourceSTypes`);

  return conversionSubTypes;
}

async function getPixelDataByDate({
  fromDate,
  toDate,
  pixelId,
  productType,
  selectedTimezone,
}: {
  fromDate: DateTime;
  toDate: DateTime;
  pixelId?: number;
  productType?: ProductType;
  selectedTimezone: TimezoneObject;
}): Promise<DataSourceStatistics[]> {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);

  const payload = {
    filterParametersStr: JSON.stringify({
      fromDate: fromDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      toDate: toDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      ...(productType && {productType}),
    }),
    ...(pixelId && {pixelId}),
  };

  const {data: pixels} = await apiService.executeGetRequest<PixelData[]>(
    `${basePath}/GetPixelsDetailsDaily`,
    payload,
  );

  return _map(pixels, toDataSourceStatisticsPixelData);
}

async function getPixelDataByHour({
  fromDate,
  toDate,
  pixelId,
  productType,
  selectedTimezone,
}: {
  fromDate: DateTime;
  toDate: DateTime;
  pixelId?: number;
  productType?: ProductType;
  selectedTimezone: TimezoneObject;
}): Promise<DataSourceStatistics[]> {
  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const params = {
    filterParametersStr: JSON.stringify({
      fromDate: fromDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      toDate: toDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      ...(productType && {productType}),
    }),
    ...(pixelId && {pixelId}),
  };

  const {data: pixels} = await apiService.executeGetRequest<PixelData[]>(
    `${basePath}/GetPixelsDetailsHourly`,
    params,
  );

  return _map(pixels, toDataSourceStatisticsPixelData);
}

async function getPixelDocumentationXlsx({
  pixelId,
  vertical,
  pixelType,
}: {
  pixelId: number;
  vertical: string;
  pixelType?: string;
}): Promise<string> {
  const {
    data: [url],
  } = await apiService.executeGetRequest<string[]>(`${basePath}/GetPixelDocumentationXlsx`, {
    pixelId,
    vertical,
    pixelType,
  });

  return url;
}

async function getMappingOptions(): Promise<DatasourceRuleOption[]> {
  const {data} = await apiV2Service.executeGetRequest<DatasourceRuleOption[]>(
    `${basePath}/fieldsmapping/options`,
  );

  return data;
}

async function getFieldsMapping(datasourceId: number): Promise<DataSourceMapping[]> {
  const {data} = await apiV2Service.executeGetRequest<DataSourceMapping[]>(
    `${basePath}/${datasourceId}/fieldsmapping`,
  );

  return data;
}

async function updateFieldsMapping(
  datasourceId: number,
  mappings: DataSourceMapping[],
): Promise<[]> {
  const {data} = await apiV2Service.executePostRequest<[]>(
    `${basePath}/${datasourceId}/fieldsmapping`,
    {datasourceId},
    {mappings},
  );

  return data;
}

async function addOfflineDataSource(params: AddDataSourceParams): Promise<number> {
  const {data: dataSourceId} = await apiV2Service.executePutRequest<number>(
    `${basePath}/OfflineEvents`,
    {},
    {...params},
  );

  return dataSourceId;
}

async function addThirdPartyDataSource(params: AddDataSourceParams): Promise<number> {
  const {data: dataSourceId} = await apiV2Service.executePutRequest<number>(
    `${basePath}/ExternalAggregatedData`,
    {},
    {...params},
  );

  return dataSourceId;
}

async function getDataSourcesStatistics(offset: number): Promise<DataSourceStatistics[]> {
  const {data: stats} = await apiV2Service.executeGetRequest<RemoteDataSourceStatistics[]>(
    `${basePath}/statistics/yesterday`,
    {offset},
  );

  return stats.map(toClientDataSourceStatistics);
}

async function getExternalConversionNames(dataSourceId: number): Promise<string[]> {
  const {data} = await apiV2Service.executeGetRequest<string[]>(
    `${basePath}/ExternalConversionNames`,
    {dataSourceId},
  );

  return data;
}

async function executeDataSourceIdentityHealthReportJob(
  datasourceId: number,
  date: DateTime,
): Promise<number> {
  const timezone = getTimeZoneStringForDateTime(UTCTimezoneObject);
  const {data} = await apiV2Service.executePostRequest<number>(
    `${basePath}/RunDataSourceIdentityHealthReportJob`,
    {},
    {datasourceId, date: date.setZone(timezone, {keepLocalTime: true}).toISO()},
  );

  return data;
}

async function executeDataSourceEtypeHealthReportJob(
  datasourceId: number,
  date: DateTime,
): Promise<number> {
  const timezone = getTimeZoneStringForDateTime(UTCTimezoneObject);
  const {data} = await apiV2Service.executePostRequest<number>(
    `${basePath}/RunDataSourceEtypeHealthReportJob`,
    {},
    {datasourceId, date: date.setZone(timezone, {keepLocalTime: true}).toISO()},
  );

  return data;
}

async function doesClientHaveActiveUserProfilingRelatedDataSource(
  clientId: number,
): Promise<boolean> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDataSource[]>(
    `${clientId}/${basePath}`,
    {},
    {includeClientId: false},
  );

  const isUserProfilingRelatedDataSources = _filter(
    data,
    ({type, status}: RemoteDataSource) =>
      isUserProfilingRelatedDataSourceType(type) && status === ObjectStatus.Active,
  );

  return !!isUserProfilingRelatedDataSources.length;
}

export default {
  get,
  getDataSources,
  deleteDataSource,
  add,
  update,
  getDatasourceSubTypes,
  getPixelDataByDate,
  getPixelDataByHour,
  getPixelDocumentationXlsx,
  getMappingOptions,
  getFieldsMapping,
  updateFieldsMapping,
  addOfflineDataSource,
  addThirdPartyDataSource,
  getDataSourcesStatistics,
  getExternalConversionNames,
  executeDataSourceIdentityHealthReportJob,
  executeDataSourceEtypeHealthReportJob,
  doesClientHaveActiveUserProfilingRelatedDataSource,
};
